import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Play, Image, Clock, ArrowLeft, Loader2, AlertCircle, Eye, Grid3X3, Settings, Heart } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getEpisodes, 
  recordEpisodeAccess, 
  addEpisodeFavorite,
  removeEpisodeFavorite,
  checkFavoriteStatus,
  type EpisodeItem, 
  type EpisodesResponse,
  getAllFavorites,
  MovieRedirectError
} from '../api/fancaps'
import { AuthenticatedImage } from '../components/AuthenticatedImage'
import { operationLogger, OperationType } from '../utils/operationLogger'

const EpisodesPage: React.FC = () => {
  const { showId } = useParams<{ showId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const title = searchParams.get('title') || '未知作品'
  const searchTerm = searchParams.get('searchTerm') || '' // 获取搜索词
  const contentType = searchParams.get('contentType') || 'anime' // 获取内容类型，默认为anime
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'default' | 'title' | 'episode'>('default')
  const [pageSize, setPageSize] = useState(20) // 每页数量
  const [displayedCount, setDisplayedCount] = useState(20) // 当前显示数量
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  
  // 🔧 确保图片URL使用ant域名而不是cdni域名，并修复路径结构
  const convertToAntDomain = (imageUrl: string): string => {
    // 🎯 修复：如果是代理URL（/api/images/xxx.jpg），直接返回，不需要转换
    if (imageUrl.startsWith('/api/images/')) {
      return imageUrl
    }
    
    if (imageUrl.includes('cdni.fancaps.net')) {
      // 提取图片ID（文件名）
      const fileName = imageUrl.split('/').pop() || ''
      return `https://ant.fancaps.net/${fileName}`
    }
    // 如果已经是ant域名但路径不对，也要修复
    if (imageUrl.includes('ant.fancaps.net') && imageUrl.includes('/file/fancaps-animeimages/')) {
      const fileName = imageUrl.split('/').pop() || ''
      return `https://ant.fancaps.net/${fileName}`
    }
    return imageUrl
  }
  
  // 收藏状态管理
  const [favoriteStates, setFavoriteStates] = useState<{ [episodeUrl: string]: boolean }>({})
  const [favoriteIds, setFavoriteIds] = useState<{ [episodeUrl: string]: string }>({}) // URL到ID的映射
  
  // 收藏操作mutation
  const favoriteMutation = useMutation({
    mutationFn: async ({ episode, isFavorited }: { episode: EpisodeItem, isFavorited: boolean }) => {
      // 记录收藏操作开始
      operationLogger.logUserAction(
        isFavorited ? '移除剧集收藏' : '添加剧集收藏',
        episode.title,
        {
          episodeUrl: episode.url,
          workTitle: title,
          workId: showId,
          currentFavoriteState: isFavorited
        }
      );
      
      if (isFavorited) {
        // 使用保存的ID来删除收藏
        const favoriteId = favoriteIds[episode.url]
        if (!favoriteId) {
          throw new Error('无法找到收藏记录ID')
        }
        
        operationLogger.info(OperationType.FAVORITE_REMOVE_EPISODE, `开始移除剧集收藏: ${episode.title}`, {
          favoriteId,
          episodeUrl: episode.url,
          workTitle: title
        });
        
        const result = await removeEpisodeFavorite(favoriteId)
        
        operationLogger.success(OperationType.FAVORITE_REMOVE_EPISODE, `成功移除剧集收藏: ${episode.title}`);
        
        return result
      } else {
        operationLogger.info(OperationType.FAVORITE_ADD_EPISODE, `开始添加剧集收藏: ${episode.title}`, {
          episodeTitle: episode.title,
          workTitle: title,
          workId: showId,
          episodeUrl: episode.url
        });
        
        const result = await addEpisodeFavorite(
          episode.title,
          title,
          showId || 'unknown',
          episode.url,
          (episode.preview_images || []).map(convertToAntDomain)
        )
        
        operationLogger.success(OperationType.FAVORITE_ADD_EPISODE, `成功添加剧集收藏: ${episode.title}`, {
          favoriteId: result.data?.id,
          episodeUrl: episode.url
        });
        
        return result
      }
    },
    onSuccess: (data, variables) => {
      const { episode, isFavorited } = variables;
      const processId = (episode as any)._processId;
      
      // 步骤3: 处理API响应
      if (processId) {
        operationLogger.logStep(processId, 3, 'API请求成功，更新本地状态', {
          responseData: data,
          newFavoriteState: !isFavorited,
          favoriteId: (data as any)?.data?.id
        }, 'start');
      }
      
      setFavoriteStates(prev => ({
        ...prev,
        [episode.url]: !isFavorited
      }))
      
      // 如果是添加收藏，保存返回的ID
      if (!isFavorited && data.success && 'data' in data && data.data) {
        const episodeData = data as { success: boolean; message: string; data: { id: string } }
        setFavoriteIds(prev => ({
          ...prev,
          [episode.url]: episodeData.data.id
        }))
      }
      
      // 如果是删除收藏，移除ID映射
      if (isFavorited) {
        setFavoriteIds(prev => {
          const newIds = { ...prev }
          delete newIds[episode.url]
          return newIds
        })
      }
      
      // 记录成功操作
      operationLogger.success(
        isFavorited ? OperationType.FAVORITE_REMOVE_EPISODE : OperationType.FAVORITE_ADD_EPISODE,
        `${isFavorited ? '移除' : '添加'}收藏成功: ${episode.title}`,
        {
          episodeUrl: episode.url,
          operation: isFavorited ? 'remove' : 'add',
          favoriteId: (data as any)?.data?.id,
          responseTime: new Date().toISOString()
        }
      );
      
      // 完成步骤3
      if (processId) {
        operationLogger.logStep(processId, 3, '本地状态更新完成', {
          finalState: !isFavorited ? '已收藏' : '未收藏'
        }, 'success');
        
        operationLogger.finishStepProcess(processId, true, 
          `${isFavorited ? '移除' : '添加'}收藏操作成功完成`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    },
    onError: (error: any, variables) => {
      const { episode, isFavorited } = variables;
      const processId = (episode as any)._processId;
      
      // 记录详细的API错误
       const requestId = `favorite_${isFavorited ? 'remove' : 'add'}_${Date.now()}`;
       
       // 增强错误对象，添加上下文信息
       const enhancedError = {
         ...error,
         context: {
           episodeData: {
             title: episode.title,
             url: episode.url,
             imageCount: episode.image_count
           },
           operation: isFavorited ? 'remove' : 'add',
           favoriteId: isFavorited ? favoriteIds[episode.url] : null,
           userAgent: navigator.userAgent,
           timestamp: new Date().toISOString()
         }
       };
       
       operationLogger.logAPIError(requestId, enhancedError);
      
      // 完成步骤流程（失败）
      if (processId) {
        operationLogger.logStep(processId, 3, 'API请求失败', {
          error: error.message,
          errorType: error.constructor.name,
          statusCode: (error as any)?.response?.status
        }, 'error');
        
        operationLogger.finishStepProcess(processId, false, 
          `${isFavorited ? '移除' : '添加'}收藏操作失败: ${error.message}`);
      }
      
      // 记录收藏操作失败
      operationLogger.logFavoriteOperation(
        isFavorited ? 'remove' : 'add',
        'episode',
        episode.url,
        episode.title
      );
      
      operationLogger.error(
        isFavorited ? OperationType.FAVORITE_REMOVE_EPISODE : OperationType.FAVORITE_ADD_EPISODE,
        `收藏操作失败: ${episode.title}`,
        {
          error: error?.message || error,
          episodeUrl: episode.url,
          operation: isFavorited ? 'remove' : 'add'
        }
      );
    }
  })
  
  // 处理收藏点击（增强版日志记录）
  const handleFavoriteClick = (episode: EpisodeItem, e: React.MouseEvent) => {
    e.stopPropagation() // 阻止触发集数点击事件
    const isFavorited = favoriteStates[episode.url] || false
    
    // 开始分步骤收藏操作流程
    const processId = operationLogger.startStepProcess(
      `${isFavorited ? '移除' : '添加'}剧集收藏: ${episode.title}`, 
      3
    );
    
    // 步骤1: 记录用户操作
    operationLogger.logStep(processId, 1, '用户点击收藏按钮', {
      action: isFavorited ? 'remove_favorite' : 'add_favorite',
      episodeTitle: episode.title,
      episodeUrl: episode.url,
      workTitle: title,
      workId: showId,
      currentState: isFavorited ? '已收藏' : '未收藏',
      targetState: isFavorited ? '移除收藏' : '添加收藏',
      clickPosition: { x: e.clientX, y: e.clientY },
      timestamp: new Date().toISOString()
    }, 'success');
    
    // 步骤2: 准备API请求
    operationLogger.logStep(processId, 2, '准备收藏API请求', {
      operation: isFavorited ? 'remove' : 'add',
      episodeData: {
        title: episode.title,
        url: episode.url,
        imageCount: episode.image_count
      },
      favoriteId: isFavorited ? favoriteIds[episode.url] : null
    }, 'start');
    
    // 记录详细的收藏操作
    operationLogger.logFavoriteOperation(
      isFavorited ? 'remove' : 'add',
      'episode',
      episode.url,
      episode.title
    );
    
    // 存储processId以便在mutation回调中使用
    (episode as any)._processId = processId;
    
    favoriteMutation.mutate({ episode, isFavorited })
  }

  // 获取剧集数据
  const {
    data: episodesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['episodes', showId],
    queryFn: async (): Promise<EpisodesResponse> => {
      if (!showId) throw new Error('缺少作品ID')
      
      // 🔧 修复：根据内容类型动态构建正确的URL路径
      // 电影使用 /movies/ 路径，TV剧集使用 /tv/ 路径，动漫使用 /anime/ 路径
      let urlPath = 'anime' // 默认为动漫
      let urlFile = 'showimages.php' // 默认为剧集列表
      
      if (contentType === 'tv') {
        urlPath = 'tv'
        urlFile = 'showimages.php'
      } else if (contentType === 'movie') {
        urlPath = 'movies'
        urlFile = 'MovieImages.php'
      }
      
      const showUrl = `https://fancaps.net/${urlPath}/${urlFile}?${showId}`
      return await getEpisodes(showUrl)
    },
    enabled: !!showId,
    retry: (failureCount, error) => {
      // 如果是电影重定向错误，不要重试
      if (error instanceof MovieRedirectError) {
        return false
      }
      return failureCount < 2
    },
    staleTime: 10 * 60 * 1000, // 10分钟缓存
  })

  // 获取收藏数据
  const { data: favoritesData } = useQuery({
    queryKey: ['favorites'],
    queryFn: getAllFavorites,
    staleTime: 30 * 1000,
  })

  // 初始化收藏状态（当收藏数据和剧集数据都加载完成后）
  useEffect(() => {
    if (favoritesData?.data?.episodes && episodesData?.episodes) {
      const newFavoriteStates: { [episodeUrl: string]: boolean } = {}
      const newFavoriteIds: { [episodeUrl: string]: string } = {}
      
      episodesData.episodes.forEach(episode => {
        // 查找匹配的收藏记录
        const favEpisode = favoritesData.data.episodes.find(fav => 
          fav.episode_url === episode.url || 
          fav.title === episode.title && fav.work_title === title
        )
        
        if (favEpisode) {
          newFavoriteStates[episode.url] = true
          newFavoriteIds[episode.url] = favEpisode.id
        } else {
          newFavoriteStates[episode.url] = false
        }
      })
      
      setFavoriteStates(newFavoriteStates)
      setFavoriteIds(newFavoriteIds)
    }
  }, [favoritesData, episodesData, title])

  // 过滤和排序的剧集列表
  const filteredEpisodes = useMemo(() => {
    if (!episodesData?.episodes) return []
    
    let filtered = episodesData.episodes
    
    // 搜索过滤
    if (localSearchTerm.trim()) {
      filtered = filtered.filter(episode => 
        episode.title.toLowerCase().includes(localSearchTerm.toLowerCase())
      )
    }
    
    // 排序
    switch (sortBy) {
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title))
        break
      case 'episode':
        filtered.sort((a, b) => (a.episode_number || 0) - (b.episode_number || 0))
        break
      default:
        // 保持默认顺序
        break
    }
    
    return filtered
  }, [episodesData?.episodes, localSearchTerm, sortBy])

  const displayedEpisodes = useMemo(() => {
    return filteredEpisodes.slice(0, displayedCount)
  }, [filteredEpisodes, displayedCount])

  // 加载更多数据
  const loadMore = useCallback(() => {
    if (displayedCount >= filteredEpisodes.length || isLoadingMore) return
    
    setIsLoadingMore(true)
    setTimeout(() => {
      setDisplayedCount(prev => Math.min(prev + pageSize, filteredEpisodes.length))
      setIsLoadingMore(false)
    }, 500) // 模拟加载延迟
  }, [displayedCount, filteredEpisodes.length, pageSize, isLoadingMore])

  // 滚动监听
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop 
          !== document.documentElement.offsetHeight) return
      loadMore()
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [loadMore])

  // 重置显示数量（搜索或排序改变时）
  useEffect(() => {
    setDisplayedCount(pageSize)
  }, [searchTerm, sortBy, pageSize])

  const handleEpisodeClick = async (episode: EpisodeItem) => {
    try {
      // 🎯 修复：直接传递完整URL，保留路径信息(/tv/ 或 /anime/)
      // URL格式: https://fancaps.net/{tv|anime}/episodeimages.php?26513-Gintama/Episode_1___2
      const episodeUrl = episode.url // 保留完整URL
      
      // 如果有搜索词，记录搜索历史（🎯 修复：传递完整URL）
      if (searchTerm && showId) {
        try {
          await recordEpisodeAccess(
            searchTerm,
            title,
            showId,
            episode.title,
            episodeUrl // 传递完整URL而不是提取的ID
          )
          console.log('✅ 搜索历史记录成功', {
            搜索词: searchTerm,
            作品: title,
            集数: episode.title
          })
        } catch (error) {
          console.warn('⚠️ 搜索历史记录失败:', error)
          // 不阻止用户继续浏览，仅记录警告
        }
      }
      
      console.log('🔗 传递完整URL到后端:', episodeUrl)
      navigate(`/images/${encodeURIComponent(episodeUrl)}?title=${encodeURIComponent(episode.title)}&workTitle=${encodeURIComponent(title)}&episodeTitle=${encodeURIComponent(episode.title)}`)
    } catch (error) {
      console.error('处理集数点击失败:', error)
      // 即使记录失败，也让用户能够继续浏览
      // 🎯 修复：即使在error情况下也传递完整URL
      console.log('🔗 传递完整URL到后端 (fallback):', episode.url)
      navigate(`/images/${encodeURIComponent(episode.url)}?title=${encodeURIComponent(episode.title)}&workTitle=${encodeURIComponent(title)}&episodeTitle=${encodeURIComponent(episode.title)}`)
    }
  }

  const handleBackClick = () => {
    navigate('/search')
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setDisplayedCount(newPageSize)
  }

  // 🎬 处理电影重定向错误
  useEffect(() => {
    if (error instanceof MovieRedirectError) {
      // 从电影URL中提取电影ID并重定向到图片页面
      const movieUrl = error.imageUrl
      // 🎯 修复：传递完整URL而不是提取ID
      console.log('🎬 检测到电影，传递完整URL:', movieUrl)
      navigate(`/images/${encodeURIComponent(movieUrl)}?title=${encodeURIComponent(title)}&workTitle=${encodeURIComponent(title)}&isMovie=true`)
    }
  }, [error, navigate, title])

  if (error) {
    // 如果是电影重定向错误，显示重定向提示
    if (error instanceof MovieRedirectError) {
      return (
        <div className="space-y-6">
          <div className="flex items-center mb-6">
            <button
              onClick={handleBackClick}
              className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回搜索
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <Play className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-blue-800 mb-2">电影内容</h3>
              <p className="text-blue-700 mb-4">
                这是一部电影，正在为您跳转到图片浏览页面...
              </p>
              <div className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                <span className="text-blue-600">重定向中...</span>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // 其他错误的处理
    return (
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBackClick}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回搜索
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        </div>
        
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">加载失败</h3>
            <p className="text-red-700 mb-4">
              {error instanceof Error ? error.message : '获取剧集列表时出现错误'}
            </p>
            <button
              onClick={() => refetch()}
              className="btn btn-outline text-red-700 border-red-300 hover:bg-red-50"
            >
              重试
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 头部导航和标题 - 添加吸顶效果 */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 -mx-6 px-6 py-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={handleBackClick}
              className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回搜索
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {episodesData?.show_title || title}
              </h1>
              {isLoading && (
                <p className="text-gray-500 flex items-center mt-1">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  加载中...
                </p>
              )}
              {!isLoading && episodesData && (
                <p className="text-gray-500 mt-1">
                  {episodesData.cached && '(缓存) '}
                  共 {episodesData.total} 集
                  {filteredEpisodes.length !== episodesData.total && 
                    ` · 筛选后 ${filteredEpisodes.length} 集`
                  }
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 搜索和筛选栏 */}
      {episodesData && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* 搜索框 */}
            <div className="flex-1">
              <input
                type="text"
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                placeholder="搜索剧集..."
                className="input w-full"
              />
            </div>
            
            {/* 排序选择 */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">排序:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="input w-auto"
              >
                <option value="default">默认顺序</option>
                <option value="title">按标题</option>
                <option value="episode">按集数</option>
              </select>
            </div>

            {/* 每页数量设置 */}
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">每页:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="input w-auto"
              >
                <option value={20}>20集</option>
                <option value={50}>50集</option>
                <option value={100}>100集</option>
              </select>
            </div>
          </div>
          
          {/* 结果计数和进度 */}
          <div className="mt-3 flex justify-between items-center text-sm text-gray-500">
            <span>
              {localSearchTerm ? (
                <>找到 {filteredEpisodes.length} 个匹配的剧集</>
              ) : (
                <>显示全部 {filteredEpisodes.length} 集</>
              )}
            </span>
            <span>
              已显示 {displayedEpisodes.length} / {filteredEpisodes.length} 集
            </span>
          </div>
        </div>
      )}

      {/* 剧集网格 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">正在获取剧集列表...</p>
          </div>
        </div>
      ) : displayedEpisodes.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayedEpisodes.map((episode, index) => (
              <div
                key={index}
                onClick={() => handleEpisodeClick(episode)}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-blue-300 hover:shadow-lg transition-all cursor-pointer group relative"
              >
                {/* 收藏按钮 - 位置固定在右上角 */}
                <div className="absolute top-3 right-3 z-10">
                  <button
                    onClick={(e) => handleFavoriteClick(episode, e)}
                    disabled={favoriteMutation.isPending}
                    className={`p-2 rounded-full transition-all shadow-sm ${
                      favoriteStates[episode.url] 
                        ? 'bg-red-500 text-white' 
                        : 'bg-white bg-opacity-90 text-gray-600 hover:bg-opacity-100 hover:text-red-500'
                    } ${favoriteMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={favoriteStates[episode.url] ? '取消收藏此集数' : '收藏此集数'}
                  >
                    <Heart className={`w-4 h-4 ${favoriteStates[episode.url] ? 'fill-current' : ''}`} />
                  </button>
                </div>
                
                {/* 剧集信息区域 */}
                <div className="p-6 h-full flex flex-col">
                  {/* 集数标签 */}
                  {episode.episode_number && (
                    <div className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded mb-3 w-fit">
                      第 {episode.episode_number} 集
                    </div>
                  )}
                  
                  {/* 剧集标题 - 固定高度，长标题显示省略号 */}
                  <h3 className="text-lg font-medium text-gray-900 mb-3 pr-12 group-hover:text-blue-600 transition-colors h-14 overflow-hidden" title={episode.title}>
                    <div className="line-clamp-2">
                      {episode.title}
                    </div>
                  </h3>
                  
                  {/* 🎯 新增：预览图片网格 - 增加高度让图片更好展示 */}
                  {episode.preview_images && episode.preview_images.length > 0 ? (
                    <div className="mb-4">
                      <div className="grid grid-cols-2 gap-2 h-32">
                        {episode.preview_images.slice(0, 4).map((imageUrl, imgIndex) => (
                          <div key={imgIndex} className="relative overflow-hidden rounded border">
                            <AuthenticatedImage
                              src={convertToAntDomain(imageUrl)}
                              alt={`${episode.title} 预览 ${imgIndex + 1}`}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              loading="lazy"
                              onError={() => {
                                // 图片加载失败时的处理逻辑已在AuthenticatedImage组件内部处理
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* 无预览图时显示占位符 - 同样增加高度 */
                    <div className="mb-4 h-32 bg-gray-100 rounded flex items-center justify-center">
                      <div className="text-center text-gray-400">
                        <Image className="w-6 h-6 mx-auto mb-1" />
                        <span className="text-xs">暂无预览</span>
                      </div>
                    </div>
                  )}
                  
                  {/* 图片数量和状态信息 */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Image className="w-4 h-4 mr-1" />
                      <span>点击查看图片</span>
                    </div>
                    {episode.preview_images && episode.preview_images.length > 0 && (
                      <span className="text-xs text-green-600">有预览</span>
                    )}
                  </div>
                  
                  {/* 查看按钮 - 固定在底部 */}
                  <div className="mt-auto">
                    <div className="flex items-center justify-center p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <Eye className="w-5 h-5 text-blue-600 mr-2" />
                      <span className="text-blue-600 font-medium">查看图片</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 加载更多指示器 */}
          {displayedCount < filteredEpisodes.length && (
            <div className="flex items-center justify-center py-8">
              {isLoadingMore ? (
                <div className="flex items-center text-blue-600">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  <span>正在加载更多...</span>
                </div>
              ) : (
                <button
                  onClick={loadMore}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  加载更多 ({filteredEpisodes.length - displayedCount} 集剩余)
                </button>
              )}
            </div>
          )}

          {/* 完成提示 */}
          {displayedCount >= filteredEpisodes.length && filteredEpisodes.length > pageSize && (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500 text-center">
                <Grid3X3 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>已显示全部 {filteredEpisodes.length} 集</p>
              </div>
            </div>
          )}
        </>
      ) : episodesData ? (
        <div className="text-center py-12">
          <Grid3X3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {localSearchTerm ? '没有找到匹配的剧集' : '暂无剧集'}
          </h3>
          <p className="text-gray-600">
            {localSearchTerm ? '尝试调整搜索关键词' : '这个作品还没有剧集信息'}
          </p>
          {localSearchTerm && (
            <button
              onClick={() => setLocalSearchTerm('')}
              className="mt-3 text-blue-600 hover:text-blue-700"
            >
              清除搜索
            </button>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default EpisodesPage